// Training Center → Semantic Model tab.
//
// Read-only port of web/scripts/admin-semantic-model.js. Four navigation
// levels:
//   1. Config list (organization × version × status)
//   2. Config detail (organization context + datasources + canonical
//      metrics + cross-datasource relationships)
//   3. Datasource detail (tables grid + description + upfront queries
//      + datasource-local relationships)
//   4. Table detail (Columns / Entities / Measures sub-tabs)
//
// Draft / Validate / Publish are wired but the deep editing forms
// (column / entity / measure / metric / relationship modals) are still
// served by the legacy admin pages. This view surfaces everything the
// old admin showed in read-only mode so audits can stay in web2.
//
// Throughout: every grid + table row is clickable end-to-end (no
// right-edge "Open →" links) per user feedback that scrolling to a
// trailing column to find the affordance is tiring.

import type { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  OrgScope,
  SMChangelogEntry,
  SMConfig,
  SMConfigDetail,
  SMCrossDsRelationship,
  SMDatasource,
  SMDatasourceDetail,
  SMEntity,
  SMMeasure,
  SMMetric,
  SMTable,
  SMTableDetail,
} from "../../api/training";
import { trainingApi } from "../../api/training";
import { AdminGrid, ErrorBlock, LoadingBlock, formatDateTime } from "../admin/_shared";

interface Props {
  bearer: string;
  scope: OrgScope;
  isSuperAdmin: boolean;
}

type View =
  | { kind: "configs" }
  | { kind: "config"; configId: number }
  | { kind: "datasource"; datasourceId: number; configId: number }
  | { kind: "table"; tableId: number; datasourceId: number; configId: number };

export function SemanticModelTab({ bearer, scope, isSuperAdmin }: Props) {
  const [view, setView] = useState<View>({ kind: "configs" });

  if (view.kind === "table") {
    return (
      <TableDetail
        bearer={bearer}
        tableId={view.tableId}
        onBack={() =>
          setView({ kind: "datasource", datasourceId: view.datasourceId, configId: view.configId })
        }
      />
    );
  }
  if (view.kind === "datasource") {
    return (
      <DatasourceDetail
        bearer={bearer}
        datasourceId={view.datasourceId}
        onBack={() => setView({ kind: "config", configId: view.configId })}
        onOpenTable={(tableId) =>
          setView({
            kind: "table",
            tableId,
            datasourceId: view.datasourceId,
            configId: view.configId,
          })
        }
      />
    );
  }
  if (view.kind === "config") {
    return (
      <ConfigDetail
        bearer={bearer}
        configId={view.configId}
        isSuperAdmin={isSuperAdmin}
        onBack={() => setView({ kind: "configs" })}
        onOpenDatasource={(dsId) =>
          setView({ kind: "datasource", datasourceId: dsId, configId: view.configId })
        }
      />
    );
  }
  return (
    <ConfigList
      bearer={bearer}
      scope={scope}
      onPick={(id) => setView({ kind: "config", configId: id })}
    />
  );
}

// ---------------------------------------------------------------------------
// Level 1 — Config list
// ---------------------------------------------------------------------------
function ConfigList({
  bearer,
  scope,
  onPick,
}: {
  bearer: string;
  scope: OrgScope;
  onPick: (id: number) => void;
}) {
  const [items, setItems] = useState<SMConfig[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    trainingApi
      .smConfigs(bearer, scope)
      .then((d) => {
        if (cancelled) return;
        setItems(d || []);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  const colDefs = useMemo<ColDef<SMConfig>[]>(
    () => [
      { field: "organization", headerName: "Organization", flex: 1 },
      {
        field: "version",
        headerName: "Version",
        width: 110,
        valueFormatter: (p) => `v${p.value}`,
        type: "numericColumn",
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        cellRenderer: (p: { value: SMConfig["status"] }) => <ConfigStatusBadge status={p.value} />,
      },
      {
        field: "published_at",
        headerName: "Published",
        flex: 1,
        valueFormatter: (p) => (p.value ? formatDateTime(p.value as string) : "—"),
      },
    ],
    [],
  );

  // Pin draft configs on top (then newest version first) so a freshly-applied,
  // not-yet-published draft is immediately visible instead of sorting to the
  // bottom by its (null) published date.
  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        const aDraft = a.status === "draft" ? 0 : 1;
        const bDraft = b.status === "draft" ? 0 : 1;
        if (aDraft !== bDraft) return aDraft - bDraft;
        return (b.version ?? 0) - (a.version ?? 0);
      }),
    [items],
  );

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (items.length === 0) {
    return (
      <div className="rounded-[14px] border border-line bg-white p-6 text-sm text-neutral-500 text-center">
        No semantic models found for the selected organization.
      </div>
    );
  }

  return (
    <AdminGrid<SMConfig>
      rowData={sortedItems}
      columnDefs={colDefs}
      rowStyle={{ cursor: "pointer" }}
      onRowClicked={(e) => {
        if (e.data) onPick(e.data.id);
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Level 2 — Config detail (org context + datasources + metrics + cross-DS rels)
// ---------------------------------------------------------------------------
function ConfigDetail({
  bearer,
  configId,
  isSuperAdmin,
  onBack,
  onOpenDatasource,
}: {
  bearer: string;
  configId: number;
  isSuperAdmin: boolean;
  onBack: () => void;
  onOpenDatasource: (dsId: number) => void;
}) {
  const [detail, setDetail] = useState<SMConfigDetail | null>(null);
  const [metrics, setMetrics] = useState<SMMetric[]>([]);
  const [changelog, setChangelog] = useState<SMChangelogEntry[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [validationMsg, setValidationMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = useCallback(() => {
    let cancelled = false;
    Promise.all([
      trainingApi.smConfigDetail(bearer, configId),
      trainingApi.smMetrics(bearer, configId).catch(() => [] as SMMetric[]),
      trainingApi.smChangelog(bearer, configId).catch(() => [] as SMChangelogEntry[]),
    ])
      .then(([d, m, cl]) => {
        if (cancelled) return;
        setDetail(d);
        setMetrics(m || []);
        setChangelog(cl || []);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, configId]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    return reload();
  }, [reload]);

  const handleCreateDraft = async () => {
    setBusy("draft");
    setError(null);
    try {
      await trainingApi.smCreateDraft(bearer, configId);
      reload();
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(null);
    }
  };

  const handleDiscardDraft = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Discard the current draft? This cannot be undone.")) return;
    setBusy("discard");
    try {
      await trainingApi.smDiscardDraft(bearer, configId);
      onBack();
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(null);
    }
  };

  const handleValidate = async () => {
    setBusy("validate");
    setValidationMsg(null);
    setError(null);
    try {
      const r = await trainingApi.smValidate(bearer, configId);
      if (r.ok) {
        setValidationMsg(`OK · ${r.warnings?.length ?? 0} warnings`);
      } else {
        setValidationMsg(`${r.errors?.length ?? 0} errors · ${r.warnings?.length ?? 0} warnings`);
      }
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(null);
    }
  };

  const handlePublish = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Publish this draft? It replaces the current published version.")) return;
    setBusy("publish");
    try {
      await trainingApi.smPublish(bearer, configId);
      reload();
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!detail) return null;

  const isDraft = detail.status === "draft";
  const isPublished = detail.status === "published";
  const rels = detail.cross_datasource_relationships ?? [];

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-neutral-500 hover:text-neutral-900"
      >
        ← All semantic models
      </button>

      <div className="flex items-start justify-between gap-4 border-b border-line pb-3">
        <div>
          <div className="text-xs text-neutral-500">{detail.organization}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <h2 className="text-base font-semibold text-neutral-900">v{detail.version}</h2>
            <ConfigStatusBadge status={detail.status} />
            {detail.published_at && (
              <span className="text-xs text-neutral-500">
                · published {formatDateTime(detail.published_at)}
                {detail.published_by ? ` by ${detail.published_by}` : ""}
              </span>
            )}
          </div>
        </div>
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            {isPublished && (
              <button
                type="button"
                onClick={handleCreateDraft}
                disabled={busy === "draft"}
                className="rounded-md border border-line px-3 h-8 text-xs hover:bg-chip disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busy === "draft" ? "Creating…" : "Create Draft"}
              </button>
            )}
            {isDraft && (
              <>
                <button
                  type="button"
                  onClick={handleValidate}
                  disabled={busy === "validate"}
                  className="rounded-md border border-line px-3 h-8 text-xs hover:bg-chip disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {busy === "validate" ? "Validating…" : "Validate"}
                </button>
                <button
                  type="button"
                  onClick={handleDiscardDraft}
                  disabled={busy === "discard"}
                  className="rounded-md border border-red-300 text-red-700 px-3 h-8 text-xs hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={busy === "publish"}
                  className="rounded-md bg-emerald-600 text-white px-3 h-8 text-xs font-medium hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Publish
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {validationMsg && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
          {validationMsg}
        </div>
      )}

      {/* Change log — what applied recommendations changed in this draft */}
      {isDraft && (
        <>
          <SectionHeader title="Change Log" count={changelog.length} />
          {changelog.length === 0 ? (
            <EmptyBlock>No applied changes yet. Apply a recommendation to populate this draft.</EmptyBlock>
          ) : (
            <ChangelogPanel entries={changelog} />
          )}
        </>
      )}

      {/* Datasources */}
      <SectionHeader title="Datasources" count={detail.datasources.length} />
      {detail.datasources.length === 0 ? (
        <EmptyBlock>No datasources configured.</EmptyBlock>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {detail.datasources.map((ds) => (
            <DatasourceCard
              key={ds.id}
              datasource={ds}
              onClick={() => onOpenDatasource(ds.id)}
            />
          ))}
        </div>
      )}

      {/* Organization Context */}
      <SectionHeader title="Organization Context" />
      {detail.organization_context ? (
        <pre className="rounded-[14px] border border-line bg-white p-4 font-mono text-xs text-neutral-800 whitespace-pre-wrap max-h-96 overflow-y-auto">
          {detail.organization_context}
        </pre>
      ) : (
        <EmptyBlock>No organization context set.</EmptyBlock>
      )}

      {/* Canonical Metrics */}
      <SectionHeader title="Canonical Metrics" count={metrics.length} />
      {metrics.length === 0 ? (
        <EmptyBlock>No canonical metrics defined.</EmptyBlock>
      ) : (
        <MetricsTable metrics={metrics} />
      )}

      {/* Cross-Datasource Relationships */}
      <SectionHeader title="Cross-Datasource Relationships" count={rels.length} />
      {rels.length === 0 ? (
        <EmptyBlock>No cross-datasource relationships defined.</EmptyBlock>
      ) : (
        <CrossDsRelationshipsTable rels={rels} />
      )}
    </div>
  );
}

function ChangelogPanel({ entries }: { entries: SMChangelogEntry[] }) {
  const fmt = (v: unknown): string => {
    if (v === null || v === undefined || v === "") return "—";
    return typeof v === "string" ? v : JSON.stringify(v, null, 2);
  };
  return (
    <div className="space-y-2">
      {entries.map((e) => {
        const d = e.applied_detail;
        const hasDiff = d && (d.before !== undefined || d.after !== undefined);
        return (
          <div key={e.id} className="rounded-[14px] border border-line bg-white p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-block rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium px-2 py-0.5">
                {d?.action || e.change_type}
              </span>
              {d?.target && (
                <span className="font-mono text-[11px] text-neutral-600">{d.target}</span>
              )}
              {d?.name && <span className="text-[11px] text-neutral-500">· {d.name}</span>}
              <span className="ml-auto text-[11px] text-neutral-400">
                {e.applied_at ? formatDateTime(e.applied_at) : ""}
                {e.applied_by ? ` · ${e.applied_by}` : ""}
              </span>
            </div>
            {e.description && <div className="mt-1 text-sm text-neutral-800">{e.description}</div>}
            {hasDiff && (
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <div className="text-[11px] font-medium text-neutral-500 mb-0.5">Before</div>
                  <pre className="overflow-x-auto rounded-md bg-red-50 text-red-900 text-[11px] leading-relaxed p-2 whitespace-pre-wrap">
                    {fmt(d?.before)}
                  </pre>
                </div>
                <div>
                  <div className="text-[11px] font-medium text-neutral-500 mb-0.5">After</div>
                  <pre className="overflow-x-auto rounded-md bg-emerald-50 text-emerald-900 text-[11px] leading-relaxed p-2 whitespace-pre-wrap">
                    {fmt(d?.after)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DatasourceCard({
  datasource,
  onClick,
}: {
  datasource: SMDatasource;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-[14px] border border-line bg-white p-4 hover:border-brand hover:shadow-sm transition-all"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-neutral-900">{datasource.name}</h3>
        {datasource.storage_type && (
          <span className="text-[10px] uppercase tracking-wide text-neutral-500">
            {datasource.storage_type}
          </span>
        )}
      </div>
      {datasource.description && (
        <p className="mt-1 text-xs text-neutral-600 line-clamp-2">{datasource.description}</p>
      )}
      <div className="mt-2 text-xs text-neutral-500 tabular-nums">
        {datasource.table_count ?? 0} table{datasource.table_count === 1 ? "" : "s"}
      </div>
    </button>
  );
}

function MetricsTable({ metrics }: { metrics: SMMetric[] }) {
  const colDefs = useMemo<ColDef<SMMetric>[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 2,
        autoHeight: true,
        wrapText: true,
        cellRenderer: (p: { data: SMMetric }) => {
          const m = p.data;
          return (
            <div className="py-1 leading-tight">
              <div className="font-medium text-neutral-900">{m.display_name || m.name}</div>
              {m.display_name && m.display_name !== m.name && (
                <div className="text-[11px] text-neutral-500 font-mono">{m.name}</div>
              )}
              {m.description && (
                <div className="text-[11px] text-neutral-600 mt-0.5 line-clamp-2">
                  {m.description}
                </div>
              )}
              {m.other_names && m.other_names.length > 0 && (
                <div className="text-[10px] text-neutral-500 mt-0.5">
                  aka: {m.other_names.join(", ")}
                </div>
              )}
            </div>
          );
        },
      },
      {
        field: "metric_type",
        headerName: "Type",
        width: 140,
        cellClass: "font-mono text-xs",
        valueFormatter: (p) => (p.value as string) ?? "—",
      },
      {
        headerName: "Source",
        flex: 1.2,
        cellClass: "font-mono text-xs",
        valueGetter: (p) => {
          const m = p.data as SMMetric;
          if (m.source_table) {
            return m.source_measure ? `${m.source_table}.${m.source_measure}` : m.source_table;
          }
          return m.calculation || "—";
        },
      },
      {
        field: "units",
        headerName: "Units",
        width: 120,
        valueFormatter: (p) => (p.value as string) || "—",
      },
      {
        field: "tracked_by",
        headerName: "Tracked by",
        width: 140,
        valueFormatter: (p) => (p.value as string) || "—",
      },
    ],
    [],
  );
  return <AdminGrid<SMMetric> rowData={metrics} columnDefs={colDefs} />;
}

function CrossDsRelationshipsTable({ rels }: { rels: SMCrossDsRelationship[] }) {
  const colDefs = useMemo<ColDef<SMCrossDsRelationship>[]>(
    () => [
      {
        headerName: "From",
        flex: 1,
        cellClass: "font-mono text-xs",
        valueGetter: (p) => fromTo(p.data as SMCrossDsRelationship, "from"),
      },
      {
        headerName: "To",
        flex: 1,
        cellClass: "font-mono text-xs",
        valueGetter: (p) => fromTo(p.data as SMCrossDsRelationship, "to"),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        valueFormatter: (p) => (p.value as string) || "—",
      },
    ],
    [],
  );
  return <AdminGrid<SMCrossDsRelationship> rowData={rels} columnDefs={colDefs} />;
}

function fromTo(rel: SMCrossDsRelationship, side: "from" | "to"): string {
  const ds = rel[`${side}_datasource` as const] as string | undefined;
  const tbl = rel[`${side}_table` as const] as string | undefined;
  const col = rel[`${side}_column` as const] as string | undefined;
  const parts: string[] = [];
  if (ds) parts.push(`${ds}.`);
  if (tbl) parts.push(tbl);
  if (col) parts.push(`.${col}`);
  return parts.join("") || "—";
}

// ---------------------------------------------------------------------------
// Level 3 — Datasource detail (tables + datasource settings + relationships)
// ---------------------------------------------------------------------------
function DatasourceDetail({
  bearer,
  datasourceId,
  onBack,
  onOpenTable,
}: {
  bearer: string;
  datasourceId: number;
  onBack: () => void;
  onOpenTable: (tableId: number) => void;
}) {
  const [detail, setDetail] = useState<SMDatasourceDetail | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    trainingApi
      .smDatasourceDetail(bearer, datasourceId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, datasourceId]);

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!detail) return null;

  const rels = (detail.relationships ?? []) as SMCrossDsRelationship[];
  const upfront = (detail.upfront_queries ?? []) as Array<{
    query?: string;
    description?: string;
  }>;

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-neutral-500 hover:text-neutral-900"
      >
        ← Config
      </button>

      <div className="border-b border-line pb-3">
        <div className="text-xs text-neutral-500">
          Datasource
          {detail.storage_type && (
            <span className="ml-2 inline-block rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] uppercase tracking-wide">
              {detail.storage_type}
            </span>
          )}
        </div>
        <h2 className="text-base font-semibold text-neutral-900 mt-0.5">{detail.name}</h2>
        {detail.description && (
          <p className="mt-1 text-sm text-neutral-600">{detail.description}</p>
        )}
      </div>

      <SectionHeader title="Tables" count={detail.tables.length} />
      {detail.tables.length === 0 ? (
        <EmptyBlock>No tables.</EmptyBlock>
      ) : (
        <TablesTable tables={detail.tables} onOpen={onOpenTable} />
      )}

      {detail.prompt_additional_instructions && (
        <>
          <SectionHeader title="Additional Prompt Instructions" />
          <pre className="rounded-[14px] border border-line bg-white p-4 font-mono text-xs text-neutral-800 whitespace-pre-wrap max-h-72 overflow-y-auto">
            {detail.prompt_additional_instructions}
          </pre>
        </>
      )}

      <SectionHeader title="Upfront Queries" count={upfront.length} />
      {upfront.length === 0 ? (
        <EmptyBlock>No upfront queries.</EmptyBlock>
      ) : (
        <div className="space-y-2">
          {upfront.map((q, i) => (
            <div key={i} className="rounded-[14px] border border-line bg-white p-3">
              {q.description && (
                <div className="text-xs text-neutral-500 mb-1">{q.description}</div>
              )}
              <pre className="font-mono text-xs text-neutral-800 whitespace-pre-wrap">
                {q.query || ""}
              </pre>
            </div>
          ))}
        </div>
      )}

      <SectionHeader title="Relationships" count={rels.length} />
      {rels.length === 0 ? (
        <EmptyBlock>No relationships defined.</EmptyBlock>
      ) : (
        <CrossDsRelationshipsTable rels={rels} />
      )}
    </div>
  );
}

function TablesTable({ tables, onOpen }: { tables: SMTable[]; onOpen: (id: number) => void }) {
  const colDefs = useMemo<ColDef<SMTable>[]>(
    () => [
      { field: "table_name", headerName: "Table", flex: 2, cellClass: "font-mono text-xs", sort: "asc" },
      {
        field: "schema_name",
        headerName: "Schema",
        flex: 1,
        cellClass: "font-mono text-xs",
        valueFormatter: (p) => (p.value as string) ?? "—",
      },
      {
        field: "label",
        headerName: "Label",
        flex: 2,
        valueGetter: (p) => {
          const t = p.data as SMTable;
          return t.label ?? t.display_name ?? "—";
        },
      },
      { field: "column_count", headerName: "Cols", width: 90, type: "numericColumn" },
      { field: "entity_count", headerName: "Entities", width: 100, type: "numericColumn" },
      { field: "measure_count", headerName: "Measures", width: 110, type: "numericColumn" },
      {
        field: "review_status",
        headerName: "Review",
        width: 130,
        valueFormatter: (p) => (p.value as string) ?? "—",
      },
    ],
    [],
  );
  // ~36px row × tables.length, capped at 540 — enough to feel like a real
  // data grid for the 50-table MeridianDemo set, not a flat list.
  const height = Math.min(540, Math.max(160, 32 + tables.length * 36));
  return (
    <AdminGrid<SMTable>
      rowData={tables}
      columnDefs={colDefs}
      height={height}
      rowStyle={{ cursor: "pointer" }}
      onRowClicked={(e) => {
        if (e.data) onOpen(e.data.id);
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Level 4 — Table detail (Columns / Entities / Measures sub-tabs)
// ---------------------------------------------------------------------------
function TableDetail({
  bearer,
  tableId,
  onBack,
}: {
  bearer: string;
  tableId: number;
  onBack: () => void;
}) {
  const [tbl, setTbl] = useState<SMTableDetail | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState<"cols" | "ents" | "meas">("cols");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    trainingApi
      .smTableDetail(bearer, tableId)
      .then((d) => {
        if (cancelled) return;
        setTbl(d);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, tableId]);

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!tbl) return null;

  // SMColumn / SMMeasure both have `[k: string]: unknown` passthroughs, so
  // spreading them can theoretically shadow `name`; build the row objects
  // by explicit field copy instead to keep types narrow.
  const columns = Object.entries(tbl.columns || {}).map(([colName, def]) => ({
    name: colName,
    type: def.type,
    description: def.description ?? null,
    primary_key: def.primary_key ?? false,
    choice_field: def.choice_field ?? null,
    foreign_key: def.foreign_key ?? null,
  }));
  const entities = tbl.entities || [];
  const measures = Object.entries(tbl.measures || {}).map(([measName, def]) => ({
    ...def,
    name: measName,
  }));

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-neutral-500 hover:text-neutral-900"
      >
        ← Datasource
      </button>

      <div className="border-b border-line pb-3">
        <div className="text-xs text-neutral-500 font-mono">
          {tbl.schema_name ? `${tbl.schema_name}.${tbl.table_name}` : tbl.table_name}
        </div>
        <h2 className="text-base font-semibold text-neutral-900 mt-0.5">
          {tbl.label || tbl.display_name || tbl.table_name}
        </h2>
        {tbl.description && (
          <p className="mt-2 text-sm text-neutral-700 whitespace-pre-wrap">{tbl.description}</p>
        )}
      </div>

      <nav className="flex items-center gap-1 border-b border-line">
        <SubTabButton active={sub === "cols"} onClick={() => setSub("cols")}>
          Columns <Count>{columns.length}</Count>
        </SubTabButton>
        <SubTabButton active={sub === "ents"} onClick={() => setSub("ents")}>
          Entities <Count>{entities.length}</Count>
        </SubTabButton>
        <SubTabButton active={sub === "meas"} onClick={() => setSub("meas")}>
          Measures <Count>{measures.length}</Count>
        </SubTabButton>
      </nav>

      {sub === "cols" && <ColumnsTable columns={columns} />}
      {sub === "ents" && <EntitiesTable entities={entities} />}
      {sub === "meas" && <MeasuresTable measures={measures} />}
    </div>
  );
}

interface ColumnRow {
  name: string;
  type?: string;
  description?: string | null;
  primary_key?: boolean;
  choice_field?: Record<string, string> | null;
  foreign_key?: { table: string; column: string } | null;
}

function ColumnsTable({ columns }: { columns: ColumnRow[] }) {
  const colDefs = useMemo<ColDef<ColumnRow>[]>(
    () => [
      { field: "name", headerName: "Column", flex: 1.5, cellClass: "font-mono text-xs", sort: "asc" },
      {
        field: "type",
        headerName: "Type",
        width: 120,
        cellClass: "font-mono text-xs",
        valueFormatter: (p) => (p.value as string) || "—",
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2.5,
        autoHeight: true,
        wrapText: true,
        valueFormatter: (p) => (p.value as string) || "—",
      },
      {
        field: "primary_key",
        headerName: "PK",
        width: 70,
        cellRenderer: (p: { value: boolean }) => (p.value ? "✓" : ""),
      },
      {
        field: "choice_field",
        headerName: "Choices",
        width: 110,
        cellRenderer: (p: { value: Record<string, string> | null }) =>
          p.value ? (
            <span
              className="inline-block rounded bg-blue-50 text-blue-700 px-1.5 py-0.5 text-[11px]"
              title={Object.entries(p.value)
                .map(([k, v]) => `${k} → ${v}`)
                .join("\n")}
            >
              {Object.keys(p.value).length} values
            </span>
          ) : (
            "—"
          ),
      },
      {
        field: "foreign_key",
        headerName: "FK",
        flex: 1.2,
        cellClass: "font-mono text-xs",
        valueGetter: (p) => {
          const fk = p.data?.foreign_key;
          return fk ? `→ ${fk.table}.${fk.column}` : "—";
        },
      },
    ],
    [],
  );
  if (columns.length === 0) return <EmptyBlock>No columns.</EmptyBlock>;
  return <AdminGrid<ColumnRow> rowData={columns} columnDefs={colDefs} />;
}

function EntitiesTable({ entities }: { entities: SMEntity[] }) {
  const colDefs = useMemo<ColDef<SMEntity>[]>(
    () => [
      { field: "type", headerName: "Type", width: 160, cellClass: "font-mono text-xs", sort: "asc" },
      {
        headerName: "Column / Expression",
        flex: 1.5,
        cellClass: "font-mono text-xs",
        valueGetter: (p) => {
          const e = p.data as SMEntity;
          return e.column || e.expression || "—";
        },
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2.5,
        autoHeight: true,
        wrapText: true,
        valueFormatter: (p) => (p.value as string) || "—",
      },
    ],
    [],
  );
  if (entities.length === 0) return <EmptyBlock>No entities.</EmptyBlock>;
  return <AdminGrid<SMEntity> rowData={entities} columnDefs={colDefs} />;
}

type MeasureRow = { name: string } & SMMeasure;

function MeasuresTable({ measures }: { measures: MeasureRow[] }) {
  const colDefs = useMemo<ColDef<MeasureRow>[]>(
    () => [
      { field: "name", headerName: "Name", flex: 1, cellClass: "font-mono text-xs", sort: "asc" },
      {
        field: "aggregation",
        headerName: "Aggregation",
        width: 140,
        cellClass: "font-mono text-xs",
        valueFormatter: (p) => (p.value as string) || "—",
      },
      {
        field: "expression",
        headerName: "Expression",
        flex: 2,
        cellClass: "font-mono text-xs",
        valueFormatter: (p) => (p.value as string) || "—",
      },
      {
        field: "units",
        headerName: "Units",
        width: 120,
        valueFormatter: (p) => (p.value as string) || "—",
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        autoHeight: true,
        wrapText: true,
        valueFormatter: (p) => (p.value as string) || "—",
      },
    ],
    [],
  );
  if (measures.length === 0) return <EmptyBlock>No measures.</EmptyBlock>;
  return <AdminGrid<MeasureRow> rowData={measures} columnDefs={colDefs} />;
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <h3 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
      {title}
      {typeof count === "number" && <Count>{count}</Count>}
    </h3>
  );
}

function Count({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block rounded-full bg-neutral-100 text-neutral-700 text-[11px] px-1.5 py-0.5 tabular-nums">
      {children}
    </span>
  );
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[14px] border border-line bg-white p-6 text-sm text-neutral-500 text-center">
      {children}
    </div>
  );
}

function SubTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px px-3 py-2 text-sm border-b-2 transition-colors ${
        active
          ? "border-brand text-neutral-900 font-medium"
          : "border-transparent text-neutral-600 hover:text-neutral-900"
      }`}
    >
      {children}
    </button>
  );
}

function ConfigStatusBadge({ status }: { status: SMConfig["status"] }) {
  const cls =
    status === "published"
      ? "bg-emerald-50 text-emerald-700"
      : status === "draft"
        ? "bg-amber-50 text-amber-700"
        : "bg-neutral-100 text-neutral-600";
  return (
    <span className={`inline-block rounded-full text-[11px] font-medium px-2 py-0.5 ${cls}`}>
      {status}
    </span>
  );
}
